import {
  Pipe,
  PipeTransform,
  OnDestroy,
  ChangeDetectorRef,
  ApplicationRef,
} from '@angular/core';
import { TranslationService } from '../appServices/translation.service';
import { Subscription } from 'rxjs';

@Pipe({
  name: 'translate',
  pure: false, // ✅ Much better performance
  standalone: false,
})
export class TranslationPipe implements PipeTransform, OnDestroy {
  private static translationCache = new Map<string, string>();
  private static currentLanguage = '';
  private static languageChangeCount = 0;
  private subscription: Subscription | null = null;
  private lastLanguageChangeCount = 0;

  constructor(
    private translationService: TranslationService,
    private appRef: ApplicationRef
  ) {
    // Subscribe to language changes and increment version for cache invalidation
    this.subscription = this.translationService.currentLanguage$.subscribe(
      (lang) => {
        if (TranslationPipe.currentLanguage !== lang) {
          TranslationPipe.currentLanguage = lang;
          TranslationPipe.languageChangeCount++;
          TranslationPipe.translationCache.clear();

          // Force global change detection for immediate updates
          this.appRef.tick();
        }
      }
    );
  }

  transform(key: string, params?: { [key: string]: string | number }): string {
    // Force re-evaluation when language changes
    const currentChangeCount = TranslationPipe.languageChangeCount;
    const currentLang = this.translationService.getCurrentLanguage();
    const cacheKey = `${currentLang}:${key}:${JSON.stringify(
      params || {}
    )}:${currentChangeCount}`;

    // Check cache first (includes language change count for cache invalidation)
    if (
      this.lastLanguageChangeCount === currentChangeCount &&
      TranslationPipe.translationCache.has(cacheKey)
    ) {
      return TranslationPipe.translationCache.get(cacheKey)!;
    }

    this.lastLanguageChangeCount = currentChangeCount;

    // Get translation and cache it
    const translation = this.translationService.translate(key, params);

    // Manage cache size (LRU-style)
    if (TranslationPipe.translationCache.size > 500) {
      const firstKey = TranslationPipe.translationCache.keys().next().value;
      if (firstKey) {
        TranslationPipe.translationCache.delete(firstKey);
      }
    }

    TranslationPipe.translationCache.set(cacheKey, translation);

    return translation;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  // Static method to clear cache if needed
  static clearCache(): void {
    TranslationPipe.translationCache.clear();
    TranslationPipe.languageChangeCount++;
  }
}
